import React, { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import Imgix from 'react-imgix'
import { Spinner } from 'react-bootstrap'
import classNames from 'classnames'
import ExperienceDeleteIcon from './ExperienceDeleteIcon'

export const JOURNEY_ROW_DND = 'JOURNEY_ROW'

const JourneyCard = ({
  journey,
  index,
  onEdit,
  onDelete,
  moveItem,
  onDragEnd,
  isUploading,
  isEditing = false,
}) => {
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: JOURNEY_ROW_DND,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current || isUploading) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) {
        return
      }
      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      moveItem(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: JOURNEY_ROW_DND,
    item: () => ({ index }),
    canDrag: !isUploading,
    end() {
      if (!isUploading && typeof onDragEnd === 'function') {
        onDragEnd()
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  const handleCardClick = () => {
    onEdit(journey, index)
  }

  const handleActionClick = (e, action) => {
    e.stopPropagation()
    action()
  }

  const section = journey.sectionTitle || journey.section || ''
  const title = journey.title || 'Untitled Section'
  const heading = section ? `${section} · ${title}` : title
  const body = journey.description || journey.body || ''

  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={classNames('experience-lrow', {
        'is-editing': isEditing,
        'is-dragging': isDragging,
      })}
      data-handler-id={handlerId}
      onClick={handleCardClick}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleCardClick()
        }
      }}>
      <span
        className='experience-grab'
        title='Drag to reorder'
        onClick={(e) => e.stopPropagation()}>
        ⋮⋮
      </span>
      {journey.imageFileName ? (
        journey.imageFileName.startsWith('data:') ? (
          <img
            src={journey.imageFileName}
            alt=''
            className='experience-lrow-thumb'
          />
        ) : (
          <Imgix
            imgixParams={{
              fit: 'crop',
              crop: 'center',
              auto: 'format,compress',
              q: 90,
            }}
            src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${journey.imageFileName}`}
            width={104}
            height={52}
            alt=''
            className='experience-lrow-thumb lazyload'
          />
        )
      ) : (
        <span className='experience-lrow-thumb' aria-hidden='true' />
      )}
      <div className='experience-lrow-copy'>
        <b>{heading}</b>
        {body ? <small>{body}</small> : null}
      </div>
      <button
        type='button'
        className='experience-x'
        title='Delete'
        aria-label='Delete'
        disabled={isUploading}
        onClick={(e) => handleActionClick(e, onDelete)}>
        {isUploading ? (
          <Spinner animation='border' size='sm' />
        ) : (
          <ExperienceDeleteIcon />
        )}
      </button>
    </div>
  )
}

export default JourneyCard
